import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AdminLogin, AdminGate } from "@/Admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  {
    n: "01",
    name: "Enterprise Engagement Architecture",
    desc: "We design end-to-end engagement frameworks that align your organization's clinical and commercial objectives with the cultural, social, and logistical realities of the patient communities you serve — translating mission into scalable, measurable action.",
  },
  {
    n: "02",
    name: "Community Intelligence & Mapping",
    desc: "Before strategy, there is understanding. We conduct deep landscape analyses of target patient populations — surfacing trust gaps, access barriers, advocacy ecosystems, and cultural dynamics that no clinical dataset alone can reveal.",
  },
  {
    n: "03",
    name: "Stakeholder Orchestration Strategy",
    desc: "We identify and align the full constellation of stakeholders — patient advocates, community organizations, clinical partners, and internal enterprise teams — into a cohesive, purpose-driven engagement model that holds together at scale.",
  },
  {
    n: "04",
    name: "Clinical Trial Community Integration",
    desc: "Trial recruitment and retention depend on community trust built long before a protocol is finalized. We help you establish authentic, lasting relationships with underserved and underrepresented populations well ahead of enrollment windows.",
  },
  {
    n: "05",
    name: "Patient Value Realization Frameworks",
    desc: "We build the metrics, milestones, and feedback mechanisms that ensure your community engagement investments translate into demonstrable health outcomes, treatment access, and long-term organizational trust — not just activity reports.",
  },
  {
    n: "06",
    name: "Executive Advisory & Training",
    desc: "We equip your senior leadership and enterprise teams with the cultural competency, community literacy, and engagement fluency needed to lead with authenticity — from the boardroom to the clinic floor and every touchpoint in between.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Community Discovery",
    body: "We begin where your organization rarely starts — with the community itself. Through structured listening sessions, ecosystem mapping, and advocacy network analysis, we build a living portrait of the populations your treatment is designed to reach.",
  },
  {
    n: "2",
    title: "Strategic Gap Analysis",
    body: "We audit your current engagement models against community realities — identifying where trust has eroded, where access remains inequitable, and where organizational pace has outrun community readiness.",
  },
  {
    n: "3",
    title: "Engagement Model Design",
    body: "We architect bespoke engagement frameworks that synchronize your enterprise objectives with community rhythms — creating structures flexible enough to honor human complexity while rigorous enough to scale across your organization.",
  },
  {
    n: "4",
    title: "Implementation & Activation",
    body: "Strategy without execution is aspiration. We partner with your teams through activation — building internal capability, facilitating community introductions, and establishing the accountability systems that sustain momentum.",
  },
  {
    n: "5",
    title: "Continuous Calibration",
    body: "Communities evolve. Treatments advance. Markets shift. We remain embedded as your strategic partner — continuously recalibrating your engagement posture to ensure you never fall behind the people who need you most.",
  },
];

const TICKER_ITEMS = [
  "Community-Centered Strategy",
  "Enterprise Engagement Architecture",
  "Patient Population Alignment",
  "Biopharma Stakeholder Orchestration",
  "Value Realization Frameworks",
  "Clinical Community Integration",
  "Life-Saving Treatment Access",
];

const SERVE_TAGS = [
  "Rare Disease Biotechs",
  "Oncology Programs",
  "Gene Therapy Developers",
  "Large-Cap Pharma",
  "Cell Therapy Organizations",
  "Specialty Biopharma",
  "Patient Advocacy Partners",
  "Clinical Research Organizations",
];

function DiscoveryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    role: "",
    focus_area: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name || form.full_name.trim().length < 2) e.full_name = "Required";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.organization || form.organization.trim().length < 1) e.organization = "Required";
    if (!form.message || form.message.trim().length < 10)
      e.message = "Tell us a bit more (10+ characters)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/discovery-call`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        role: form.role.trim() || null,
        focus_area: form.focus_area || null,
        message: form.message.trim(),
      });
      toast.success("Discovery request received", {
        description: "A PACE strategist will reach out within one business day.",
      });
      setForm({
        full_name: "",
        email: "",
        organization: "",
        role: "",
        focus_area: "",
        message: "",
      });
      onOpenChange(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail) &&
          err.response.data.detail[0]?.msg) ||
        "Something went wrong. Please try again.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="pace-dialog" data-testid="discovery-dialog">
        <DialogHeader>
          <div className="pace-dialog-eyebrow">— Discovery Session</div>
          <DialogTitle className="pace-dialog-title">
            Schedule a <em>Discovery Call</em>
          </DialogTitle>
          <DialogDescription className="pace-dialog-desc">
            Share a few details and a PACE Enterprise Engagement Strategist will be in
            touch to align on objectives and next steps.
          </DialogDescription>
        </DialogHeader>

        <form className="pace-form" onSubmit={handleSubmit} data-testid="discovery-form" noValidate>
          <div className="pace-form-row">
            <div className="pace-field">
              <label htmlFor="full_name">Full Name</label>
              <input
                id="full_name"
                data-testid="form-full-name"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="Dr. Jane Doe"
                autoComplete="name"
              />
              {errors.full_name && (
                <span className="pace-error" data-testid="error-full-name">
                  {errors.full_name}
                </span>
              )}
            </div>
            <div className="pace-field">
              <label htmlFor="email">Work Email</label>
              <input
                id="email"
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="jane@biopharma.com"
                autoComplete="email"
              />
              {errors.email && (
                <span className="pace-error" data-testid="error-email">
                  {errors.email}
                </span>
              )}
            </div>
          </div>

          <div className="pace-form-row">
            <div className="pace-field">
              <label htmlFor="organization">Organization</label>
              <input
                id="organization"
                data-testid="form-organization"
                value={form.organization}
                onChange={(e) => update("organization", e.target.value)}
                placeholder="Acme Biotherapeutics"
              />
              {errors.organization && (
                <span className="pace-error" data-testid="error-organization">
                  {errors.organization}
                </span>
              )}
            </div>
            <div className="pace-field">
              <label htmlFor="role">Role (optional)</label>
              <input
                id="role"
                data-testid="form-role"
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
                placeholder="VP, Patient Engagement"
              />
            </div>
          </div>

          <div className="pace-field">
            <label htmlFor="focus_area">Therapeutic Focus (optional)</label>
            <select
              id="focus_area"
              data-testid="form-focus-area"
              value={form.focus_area}
              onChange={(e) => update("focus_area", e.target.value)}
            >
              <option value="">— Select an area —</option>
              <option value="Rare Disease">Rare Disease</option>
              <option value="Oncology">Oncology</option>
              <option value="Gene Therapy">Gene Therapy</option>
              <option value="Cell Therapy">Cell Therapy</option>
              <option value="Specialty Biopharma">Specialty Biopharma</option>
              <option value="Large-Cap Pharma">Large-Cap Pharma</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="pace-field">
            <label htmlFor="message">What would you like to discuss?</label>
            <textarea
              id="message"
              data-testid="form-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Briefly describe your engagement objectives, current challenges, or upcoming milestones."
            />
            {errors.message && (
              <span className="pace-error" data-testid="error-message">
                {errors.message}
              </span>
            )}
          </div>

          <div className="pace-form-actions">
            <button
              type="button"
              className="pace-form-cancel"
              onClick={() => onOpenChange(false)}
              data-testid="form-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              data-testid="form-submit"
            >
              <span>{submitting ? "Submitting..." : "Request Discovery Call"}</span>
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Home = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);

  // Render ticker items twice for seamless loop
  const tickerLoop = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="pace-root" data-testid="pace-landing">
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0d1f3c",
            color: "#f5f0e8",
            border: "1px solid rgba(201,150,58,0.25)",
            fontFamily: "'DM Mono', monospace",
            fontSize: "0.8rem",
          },
        }}
      />

      {/* NAV */}
      <nav className="pace-nav" data-testid="nav">
        <div className="nav-logo">
          PACE <span>Consulting</span>
        </div>
        <ul className="nav-links">
          <li>
            <a href="#mission" data-testid="nav-mission">
              Mission
            </a>
          </li>
          <li>
            <a href="#services" data-testid="nav-services">
              Services
            </a>
          </li>
          <li>
            <a href="#approach" data-testid="nav-approach">
              Approach
            </a>
          </li>
          <li>
            <a href="#serve" data-testid="nav-serve">
              Who We Serve
            </a>
          </li>
        </ul>
        <button className="nav-cta" onClick={openDialog} data-testid="nav-engage-btn">
          Engage With Us
        </button>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="hero-grid-lines" />
        <div className="hero-left">
          <div className="hero-eyebrow">Patient Anchored Consulting Experts</div>
          <h1 className="hero-title">
            Keeping Pharma at the
            <br />
            <em>Pace of the People</em>
            <br />
            It Exists to Serve
          </h1>
          <p className="hero-subtitle">
            We architect enterprise engagement strategies that synchronize pharmaceutical
            and biopharma organizations with the living, breathing communities at the
            center of every life-saving treatment — ensuring the science never outpaces
            the story.
          </p>
          <div className="hero-actions">
            <button
              className="btn-primary"
              onClick={openDialog}
              data-testid="hero-discovery-btn"
            >
              <span>Schedule a Discovery Call</span>
            </button>
            <a href="#services" className="btn-link" data-testid="hero-services-link">
              Explore Our Services
            </a>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-visual">
            <div className="pulse-ring" />
            <div className="pulse-ring" />
            <div className="pulse-ring" />
            <div className="hero-center-orb">
              <span className="pace-acronym">PACE</span>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="ticker-bar" data-testid="ticker">
        <div className="ticker-track">
          {tickerLoop.map((item, i) => (
            <span className="ticker-item" key={i}>
              <span>◆</span> {item}
            </span>
          ))}
        </div>
      </div>

      {/* MISSION */}
      <section className="mission" id="mission">
        <div>
          <div className="section-label">Our Purpose</div>
          <h2 className="section-title">
            Science Moves Fast.
            <br />
            <em>Communities Move Together.</em>
          </h2>
          <div className="mission-body">
            <p>
              Pharmaceutical and biopharma organizations develop breakthroughs at
              remarkable velocity — but{" "}
              <strong>
                the communities who need those treatments most are rarely moving at the
                same pace.
              </strong>{" "}
              Gaps in trust, access, representation, and communication compound into
              barriers that slow adoption and erode impact.
            </p>
            <p>
              PACE exists to close that distance. We are a boutique consulting firm that
              embeds <strong>patient-anchored intelligence</strong> directly into
              enterprise strategy — ensuring that the communities at the center of your
              clinical mission are not an afterthought, but the organizing principle of
              everything you build.
            </p>
            <p>
              From enterprise engagement model design to scalable community integration
              frameworks, we translate the lived reality of patient populations into{" "}
              <strong>
                actionable strategy your organization can execute with confidence and
                integrity.
              </strong>
            </p>
          </div>
        </div>
        <div className="mission-stats">
          <div className="stat-card">
            <span className="stat-number">P</span>
            <div className="stat-label">
              Patient-First
              <br />
              Perspective
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-number">A</span>
            <div className="stat-label">
              Anchored in
              <br />
              Community Truth
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-number">C</span>
            <div className="stat-label">
              Consulting with
              <br />
              Clinical Precision
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-number">E</span>
            <div className="stat-label">
              Expertise That
              <br />
              Drives Equity
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services" id="services">
        <div className="services-header">
          <div className="section-label">What We Do</div>
          <h2 className="section-title">
            Enterprise Strategy
            <br />
            <em>Rooted in Human Reality</em>
          </h2>
        </div>
        <div className="services-grid">
          {SERVICES.map((s) => (
            <div className="service-card" key={s.n} data-testid={`service-${s.n}`}>
              <div className="service-num">{s.n}</div>
              <div className="service-name">{s.name}</div>
              <p className="service-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APPROACH */}
      <section className="approach" id="approach">
        <div className="section-label">How We Work</div>
        <h2 className="section-title">
          Methodology Built on
          <br />
          <em>Proximity, Not Assumption</em>
        </h2>
        <div className="approach-grid">
          <div className="approach-steps">
            {STEPS.map((s) => (
              <div className="step" key={s.n} data-testid={`step-${s.n}`}>
                <div className="step-num">{s.n}</div>
                <div>
                  <div className="step-title">{s.title}</div>
                  <p className="step-body">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="approach-quote">
              <p className="quote-text">
                "The communities living with these diseases are not waiting on the
                science. They have been waiting on us — to listen, to show up, and to
                stay."
              </p>
              <div className="quote-attr">— PACE Founding Principle</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVE */}
      <section className="serve" id="serve">
        <div className="serve-header">
          <div className="section-label" style={{ justifyContent: "center" }}>
            Who We Serve
          </div>
          <h2 className="section-title">
            Built for Organizations
            <br />
            <em>Building What Matters</em>
          </h2>
          <p className="serve-desc">
            PACE partners with pharmaceutical and biopharma organizations at every stage
            of the treatment lifecycle — from early-stage biotech companies conducting
            first-in-human trials, to established pharmaceutical enterprises navigating
            post-approval community adoption. If you are developing or delivering
            life-saving treatment, there is a community depending on you to get this
            right. We help you do exactly that.
          </p>
          <div className="serve-tags">
            {SERVE_TAGS.map((t) => (
              <span className="serve-tag" key={t}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="section-label" style={{ justifyContent: "center" }}>
            Ready to Close the Distance?
          </div>
          <h2 className="cta-title">
            The Community Is
            <br />
            Already <em>Moving.</em>
            <br />
            Are You?
          </h2>
          <p className="cta-sub">
            Schedule a no-obligation discovery session with our Enterprise Engagement
            Strategists. We will help you understand where the gaps exist — and what it
            takes to close them with integrity, precision, and pace.
          </p>
          <div className="cta-buttons">
            <button
              className="btn-primary"
              onClick={openDialog}
              data-testid="cta-discovery-btn"
            >
              <span>Book a Discovery Session</span>
            </button>
            <a
              href="mailto:hello@paceconsulting.com"
              className="btn-link"
              data-testid="cta-contact-link"
            >
              Contact Our Team
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="pace-footer">
        <div className="footer-logo">PACE</div>
        <div className="footer-tagline">
          Keeping pharma at the pace of the people.
        </div>
        <div className="footer-copy">
          © 2026 Patient Anchored Consulting Experts. All rights reserved.
        </div>
      </footer>

      <DiscoveryDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminGate />} />
          <Route path="/admin/login" element={<AdminLogin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
